import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconThemableProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmailOutline from "@ucs/Icons/EmailOutlineThin";
import LocationOutlineThin from "@ucs/Icons/LocationOutlineThin";
import PhoneOutline from "@ucs/Icons/PhoneOutline";
import SendOutlineThin from "@ucs/Icons/SendOutlineThin";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import { css } from "styled-components";

export interface ActionTileStyles {
    card?: InjectableCss;
    iconItem?: IconThemableProps;
    contentItem?: InjectableCss;
    titleItem?: InjectableCss;
    subtitleItem?: InjectableCss;
    ctaItem?: ButtonProps;
}

export const cardContainerStyles: ActionTileStyles = {
    card: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 38px 6px;
            background: ${UCSColors.gray10};
            box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.05);
            min-height: 280px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: row;
                        align-items: flex-start;
                        padding: 24px 6px;
                        min-height: 50px;
                    `,
                    css`
                        padding: 28px 6px;
                    `,
                ]);
            }}
        `,
    },
    iconItem: {
        color: UCSColors.gray07,
        css: css`
            margin-bottom: 28px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-left: 15px;
                        margin-right: 20px;
                        svg {
                            margin-bottom: 8.5px;
                        }
                    `,
                    css`
                        margin-bottom: 14px;
                    `,
                ]);
            }}
        `,
    },
    contentItem: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        align-items: flex-start;
                        padding-right: 7px;
                        height: auto;
                        gap: 8px;
                    `,
                    css`
                        padding: 4px;
                    `,
                ]);
            }}
        `,
    },
    titleItem: {
        css: css`
            margin-bottom: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 0;
                    `,
                    css`
                        margin-bottom: 4px;
                    `,
                ]);
            }}
        `,
    },
    subtitleItem: {
        css: css`
            font-weight: 400;
            text-align: center;
            line-height: 17px;
        `,
    },
    ctaItem: {
        variant: "secondary",
        sizeVariant: "small",
        typographyProps: { transform: "uppercase" },
        css: css`
            margin-top: auto;
        `,
    },
};

interface OwnProps {
    icon: string;
    title: string;
    info: string;
    ctaMessage: string;
    ctaFunc: any;
    extendedStyles?: ActionTileStyles;
}

const enum icons {
    email = "email",
    location = "location",
    phone = "phone",
    send = "send",
}

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.email:
            return EmailOutline;
        case icons.location:
            return LocationOutlineThin;
        case icons.phone:
            return PhoneOutline;
        case icons.send:
            return SendOutlineThin;
        default:
            break;
    }

    return "";
};

const ContactActionTile: FC<OwnProps> = ({ icon, title, info, ctaMessage, ctaFunc, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(cardContainerStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.card}>
            <Hidden above="xs">
                <Icon src={GetIcon(icon)} height={50} width={50} {...styles.iconItem}></Icon>
            </Hidden>
            <Hidden below="sm" style={{ height: "100px" }}>
                <Icon src={GetIcon(icon)} width={80} height={80} {...styles.iconItem}></Icon>
            </Hidden>
            <StyledWrapper {...styles.contentItem}>
                <Typography variant="h4" {...styles.titleItem}>
                    {title}
                </Typography>
                <Typography size={14} {...styles.subtitleItem}>
                    {info}
                </Typography>
                <Button {...styles.ctaItem} onClick={ctaFunc}>
                    {ctaMessage}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ContactActionTile;
